import React from "react";

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import ProjectAdminForm from "./projectAdminForm/ProjectAdminForm";
import { UPDATE_PROJECT } from "../../actions/ActionTypes";
import { notifError } from "../../actions/FlashMessageActions";
import { putApi } from "../../actions/SendActions";
import { validateSocialNetworkLinks } from "../../utils/data/Project";

import type { JSX } from "react";

import type { Project, UpdatedProject } from "../../api/ProjectAPI.types";
import type { AppState } from "../../reducers";

function ProjectAdminUpdate(): JSX.Element {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { t } = useTranslation("editor-project");
  const dispatch = useDispatch();

  const projects = useSelector(
    (state: AppState) => state.fetchReducer.projects,
  );
  const token = useSelector((state: AppState) => state.loginReducer.token);

  const currentProject = projects.data.find(
    (project) => project.name === projectSlug,
  );

  if (currentProject) {
    if (!currentProject.storefront_project_logins) {
      currentProject.storefront_project_logins = [];
    }
    if (!currentProject.trackers) {
      currentProject.trackers = [];
    }
    if (!currentProject.social_network_links) {
      currentProject.social_network_links = [];
    }
    if (!currentProject.page_links) {
      currentProject.page_links = [];
    }
  }

  const projectIsValid = (item: UpdatedProject): string[] => {
    const errorMessages: string[] = [];

    validateSocialNetworkLinks(item.social_network_links, t, errorMessages);

    if (!item.name || item.name.length <= 0) {
      errorMessages.push(t("editor-errors:noURL"));
    }

    item.translations.forEach((translation) => {
      if (!translation.title) {
        errorMessages.push(t("editor-errors:noTitle"));
      }
    });

    return errorMessages;
  };

  const onUpdate = (project: Project | UpdatedProject): void => {
    // Format model for saving
    const projectTranslationsUpdated = project.translations.map(
      (translation) => {
        if ("uuid" in translation && !translation.uuid) {
          const { uuid, ...translationWithoutUuid } = translation;
          return translationWithoutUuid;
        }
        return translation;
      },
    );

    const projectUpdated = {
      ...project,
      translations: projectTranslationsUpdated,
    };

    const errorMessages = projectIsValid(projectUpdated);
    if (errorMessages.length === 0) {
      dispatch(
        putApi({
          endpoint: "/api/project/v3/admin/storefront_projects",
          type: UPDATE_PROJECT,
          headers: [
            {
              key: "xx-ok-project",
              value: projectSlug,
            },
          ],
          item: "project",
          token,
          data: projectUpdated,
          message: t("editor-notif:saveSuccess"),
          error: t("editor-notif:saveError"),
        }),
      );
    } else {
      dispatch(notifError(t("editor-notif:saveError"), errorMessages));
    }
  };

  return (
    <div className="editor-smartlist-update-container">
      {currentProject ? (
        <ProjectAdminForm
          hideBackButton={false}
          itemIsValid={projectIsValid}
          onSubmit={onUpdate}
          project={currentProject}
          type="edit"
        />
      ) : null}
    </div>
  );
}

export default ProjectAdminUpdate;
